import { useEffect, useRef, useState } from 'react';
import { api } from './api';

export interface Meter {
  score: number;
  acceptable: boolean;
  warning: string;
  suggestions: string[];
  /** False until a score has come back for the current password. */
  ready: boolean;
  /** True when the check could not run; the meter stays quiet rather than lying. */
  unavailable: boolean;
}

const EMPTY: Meter = {
  score: 0,
  acceptable: false,
  warning: '',
  suggestions: [],
  ready: false,
  unavailable: false,
};

export const MIN_SCORE = 3;
export const MIN_LENGTH = 12;
export const SCORE_LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'] as const;

/**
 * Password strength, scored on the server.
 *
 * The obvious implementation runs zxcvbn in the browser — but its dictionary
 * is roughly 230KB gzipped, which is more than the entire §0 budget for
 * browser JavaScript, and it would land on the register screen where the app
 * has done nothing for the user yet.
 *
 * Scoring server-side removes that weight completely and has a second benefit:
 * the meter and the API can no longer disagree, because there is only one
 * implementation. The candidate password goes over TLS in a POST body — the
 * same way it does on submit — and the endpoint neither stores nor logs it.
 *
 * The meter is guidance either way. `POST /api/auth/register` re-scores the
 * password itself and is the binding check.
 */
export function usePasswordMeter(password: string, email?: string): Meter {
  const [meter, setMeter] = useState<Meter>(EMPTY);
  const requestId = useRef(0);

  useEffect(() => {
    if (!password) {
      setMeter(EMPTY);
      return;
    }

    const id = ++requestId.current;
    let cancelled = false;

    // Debounced so a fast typist makes one request, not thirty.
    const timer = setTimeout(() => {
      api
        .passwordStrength(password, email)
        .then((result) => {
          // Ignore a slow response that a newer keystroke has superseded.
          if (cancelled || id !== requestId.current) return;
          setMeter({
            score: result.score,
            acceptable: result.acceptable,
            warning: result.warning,
            suggestions: result.suggestions,
            ready: true,
            unavailable: false,
          });
        })
        .catch(() => {
          if (cancelled || id !== requestId.current) return;
          // Rate-limited or offline. Say nothing rather than showing a score
          // that is not real — the server still enforces the requirement.
          setMeter({ ...EMPTY, unavailable: true });
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [password, email]);

  return meter;
}
